import { AtSign, Bot, Briefcase, FileText, Forward, PenLine } from "lucide-react";
import { FilterSelect, Toolbar } from "../../ui/Toolbar";
import "./inbox.css";
import { Notifications } from "./Notifications";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getAiGlobalSettings,
  getAiPreferences,
  listInbox,
  listInboxTasks,
  listPeople,
  quickRecord,
  returnInbox,
  triageInbox,
  type InboxItem,
  type InboxTask,
} from "../../api/hub/ai";
import {
  Empty,
  Field,
  MutationState,
  Page,
  QueryState,
  SourceLink,
  Tabs,
  displayDate,
  useHubMutation,
} from "../focus/shared";
export const SOURCE_LABELS = {
  agent: "个人代理提交",
  mention: "群里 @机器人",
  forward: "转发消息",
  crm: "CRM 新线索",
  quick: "页面一句话",
};
const SOURCE_ICONS = { agent: Bot, mention: AtSign, forward: Forward, crm: Briefcase, quick: PenLine };
const STATUS_LABELS = {
  pending: "待整理",
  processing: "正在整理",
  triaged: "已整理",
  returned: "已退回，待重新整理",
};
export function inboxReasonLabel(item: Pick<InboxItem, "status" | "reason">) {
  if (item.status === "returned") return "退回原因";
  if (item.reason?.startsWith("只建议：")) return "分拣建议";
  return item.status === "triaged" ? "分拣依据" : "处理说明";
}
function InboxRow({
  item,
  tasks,
  names,
  suggestionsOnly,
  aiEnabled,
  aiReady,
}: {
  item: InboxItem;
  tasks: InboxTask[];
  names: Map<string, string>;
  suggestionsOnly: boolean;
  aiEnabled: boolean;
  aiReady: boolean;
}) {
  const [reason, setReason] = useState(""),
    [returning, setReturning] = useState(false);
  const action = useHubMutation(
    async (type: "triage" | "return") =>
      type === "triage" ? triageInbox(item.id) : returnInbox(item.id, reason),
    () => setReturning(false),
  );
  const hasSuggestion = item.reason?.startsWith("只建议：");
  const source = String(item.source),
    isAgent = source === "agent",
    legacy = !Object.hasOwn(SOURCE_LABELS, source);
  const waiting = item.taskIds.some(
    (id) => tasks.find((t) => t.id === id)?.status === "triage",
  );
  const triageLabel = hasSuggestion
    ? "已生成分拣建议"
    : suggestionsOnly
      ? "生成分拣建议"
      : "整理后待本人确认";
  const Icon = SOURCE_ICONS[source as keyof typeof SOURCE_ICONS] ?? FileText;
  return (
    <article className="lc-row ib-row" data-status={item.status}>
      <span className="ib-src" aria-hidden="true"><Icon /></span>
      <div className="lc-meta ib-meta">
        <span className="lc-badge">
          {SOURCE_LABELS[source as keyof typeof SOURCE_LABELS] ?? "历史来源"}
        </span>
        {STATUS_LABELS[item.status]} · {displayDate(item.createdAt)}
      </div>
      <h2>{item.title}</h2>
      <p className="ib-content">{item.content}</p>
      <SourceLink href={item.sourceUrl} />
      {isAgent && (
        <p className="lc-muted">代理已拆好任务，只查重、补空字段，不再拆分。</p>
      )}
      {legacy && (
        <p className="lc-muted">
          历史记录只供核对；新任务请通过个人代理或页面一句话提交。
        </p>
      )}
      {item.reason && (
        <p>
          {inboxReasonLabel(item)}：
          {hasSuggestion ? item.reason.slice("只建议：".length) : item.reason}
        </p>
      )}
      {hasSuggestion && (
        <p className="lc-muted">
          建议尚未执行。请到 <a href="/ai-actions">AI 动作账本</a>
          核对历史建议；新任务确认在首页处理。
        </p>
      )}
      {item.decisionDraftStatus === "pending" && (
        <p role="status">历史决策草稿状态待核对。新的结论在拍板后自动记录。</p>
      )}
      {item.decisionDraftStatus === "failed" && (
        <p role="status">
          历史决策草稿起草失败。{item.decisionDraftError}{" "}
          新的结论在拍板后自动记录。
        </p>
      )}
      {item.taskIds.length > 0 && (
        <div>
          <p>{waiting ? "待本人确认的任务：" : "关联任务："}</p>
          {item.taskIds.map((id) => {
            const task = tasks.find((t) => t.id === id);
            return (
              <div key={id}>
                <a href={`/tasks?task=${encodeURIComponent(id)}`}>
                  {task ? `${task.identifier ?? ""} ${task.title}`.trim() : id}
                </a>
                {task && (
                  <p className="lc-meta">
                    负责人{" "}
                    {task.assigneeId
                      ? `${names.get(task.assigneeId) ?? "待核对人员"}${task.assigneeType === "agent" ? "的 AI" : ""}`
                      : "未指派"}{" "}
                    · {task.status === "triage" ? "待本人确认 · " : ""}
                    {["未定", "紧急", "高", "中", "低"][task.priority ?? 0]} ·
                    截止 {displayDate(task.dueDate)} ·{" "}
                    {task.domain || "未设置业务域"}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="lc-actions">
        {!isAgent && !legacy && item.status !== "triaged" && (
          <button
            disabled={
              action.isPending ||
              item.status === "processing" ||
              hasSuggestion ||
              !aiEnabled ||
              !aiReady
            }
            onClick={() => action.mutate("triage")}
          >
            {!aiReady
              ? "读取 AI 设置中"
              : aiEnabled
                ? triageLabel
                : "AI 已关闭"}
          </button>
        )}
        {!isAgent && item.taskIds.length > 0 && <a href="/">去首页等我拍板</a>}
        {!legacy && item.status === "triaged" && item.taskIds.length > 0 && (
          <button onClick={() => setReturning((v) => !v)}>不是我的</button>
        )}
      </div>
      {returning && (
        <form
          className="lc-form"
          onSubmit={(e) => {
            e.preventDefault();
            action.mutate("return");
          }}
        >
          <Field label="退回原因">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="说明应该由谁处理，帮助重新整理"
            />
          </Field>
          <button disabled={action.isPending || !reason.trim()}>退回收件箱</button>
        </form>
      )}
      <MutationState mutation={action} />
    </article>
  );
}
export function InboxPage() {
  const tasks = useQuery({
      queryKey: ["hub", "tasks", "inbox-results"],
      queryFn: listInboxTasks,
    }),
    people = useQuery({ queryKey: ["hub", "people"], queryFn: listPeople }),
    preferences = useQuery({
      queryKey: ["hub", "ai-preferences"],
      queryFn: getAiPreferences,
    }),
    globalSettings = useQuery({
      queryKey: ["hub", "ai-settings"],
      queryFn: getAiGlobalSettings,
    }),
    items = useQuery({ queryKey: ["hub", "inbox"], queryFn: listInbox });
  const names = new Map(people.data?.map((p) => [p.id, p.name]));
  const [tab, setTab] = useState("pending"),
    [source, setSource] = useState(""),
    [compose, setCompose] = useState(false),
    [draft, setDraft] = useState({ title: "", content: "", sourceUrl: "" });
  const create = useHubMutation(quickRecord, () => {
    setCompose(false);
    setDraft({ title: "", content: "", sourceUrl: "" });
  });
  const filtered =
    items.data?.filter(
      (item) =>
        (tab === "all" ||
          ["pending", "returned", "processing"].includes(item.status) ||
          item.taskIds.some(
            (id) => tasks.data?.find((t) => t.id === id)?.status === "triage",
          )) &&
        (!source || item.source === source),
    ) ?? [];
  const pendingCount = (items.data ?? []).filter((item) => ["pending", "returned", "processing"].includes(item.status)).length;
  return (
    <Page
      title="收件箱"
      action={
        <button className="lc-primary" onClick={() => setCompose((v) => !v)}>
          一句话记事
        </button>
      }
    >
      {compose && (
        <form
          className="lc-form"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate({
              ...draft,
              title: draft.content.trim().slice(0, 80),
            });
          }}
        >
          <Field label="原文 / 想做的事">
            <input
              autoFocus
              value={draft.content}
              onChange={(e) => setDraft({ ...draft, content: e.target.value })}
              placeholder="说一句要做什么…"
            />
          </Field>
          <button disabled={!draft.content.trim() || create.isPending}>
            放进收件箱
          </button>
          <MutationState mutation={create} />
        </form>
      )}
      {(preferences.isError || globalSettings.isError) && (
        <p role="alert">AI 设置读取失败，请刷新后再整理。</p>
      )}
      {tasks.isError && (
        <p role="alert">关联任务读取失败，请刷新核对确认状态。</p>
      )}
      <Toolbar
        label="收件箱筛选"
        end={
          tab !== "notifications" && (
            <FilterSelect label="来源" allLabel="全部五种" value={source} onChange={setSource}>
              {Object.entries(SOURCE_LABELS).map(([id, title]) => (
                <option key={id} value={id}>
                  {title}
                </option>
              ))}
            </FilterSelect>
          )
        }
      >
        <Tabs
          label="收件箱状态"
          value={tab}
          set={setTab}
          options={[
            ["pending", `待处理${pendingCount ? ` ${pendingCount}` : ""}`],
            ["all", "全部记录"],
            ["notifications", "通知"],
          ]}
        />
      </Toolbar>
      {tab === "notifications" ? (
        <Notifications />
      ) : (
        <>
          <QueryState query={items}>
            {filtered.length ? (
              filtered.map((item) => (
                <InboxRow
                  key={item.id}
                  item={item}
                  tasks={tasks.data ?? []}
                  names={names}
                  suggestionsOnly={
                    !!(
                      preferences.data?.suggestionsOnly ||
                      globalSettings.data?.suggestionsOnly
                    )
                  }
                  aiEnabled={
                    !!(
                      preferences.data?.enabled && globalSettings.data?.enabled
                    )
                  }
                  aiReady={!!(preferences.data && globalSettings.data)}
                />
              ))
            ) : (
              <Empty>
                这个范围没有记录。可以说一句要做的事，或切换全部记录核对。
              </Empty>
            )}
          </QueryState>
        </>
      )}
    </Page>
  );
}
